import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getEventById } from "../../services/eventService";
import { getGalleriesByEvent, updateEventGallery, archiveEventGallery, updateGalleryOrder } from "../../services/galleryService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import CreateGalleryModal from "../../components/common/CreateGalleryModal";
import WatermarkSettingsModal from "../../components/common/WatermarkSettingsModal";
import ConfirmModal from "../../components/common/ConfirmModal";
import EditNameModal from "../../components/common/EditNameModal";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableGalleryCard = ({ gallery, onEdit, onDelete, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: gallery.Id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...styles.galleryCard, ...style }}
      onClick={onClick}
      {...attributes}
      {...listeners}
    >
      <div style={{ ...styles.colorBar, background: gallery.IsPremium ? "#f59e0b" : "#6c63ff" }} />
      <div style={styles.galleryBody}>
        <div style={styles.galleryTopRow}>
          <span style={styles.galleryName}>{gallery.Name}</span>
          <div style={styles.galleryActions}>
            {gallery.IsPremium && <span style={styles.premiumBadge}>⭐ Premium</span>}
            <button
              style={styles.cardActionBtn}
              onClick={(e) => { e.stopPropagation(); onEdit(gallery); }}
              title="Edit gallery name"
            >
              ✏
            </button>
            <button
              style={{ ...styles.cardActionBtn, ...styles.deleteActionBtn }}
              onClick={(e) => { e.stopPropagation(); onDelete(gallery); }}
              title="Delete gallery"
            >
              🗑
            </button>
          </div>
        </div>
        <div style={styles.galleryCounts}>
          <span>🖼️ {gallery.MediaCount} media</span>
          <span>📁 {gallery.AlbumCount} albums</span>
        </div>
        {gallery.Package && (
          <div style={styles.packageName}>📦 {gallery.Package.Name}</div>
        )}
        <div style={styles.galleryMeta}>
          {gallery.IsWatermark && <span style={styles.tag}>Watermark</span>}
          {gallery.IsMediaHandlesGallery && <span style={styles.tag}>Media Handles</span>}
          {gallery.IsUserGallery && <span style={styles.tag}>User Gallery</span>}
        </div>
      </div>
    </div>
  );
};


const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAssigned = !!location.state?.isAssigned;
  const [event, setEvent] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [loadingGalleries, setLoadingGalleries] = useState(true);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [showWatermark, setShowWatermark] = useState(false);
  const [editGalleryTarget, setEditGalleryTarget] = useState(null);
  const [deleteGalleryTarget, setDeleteGalleryTarget] = useState(null);
  const [editGalleryName, setEditGalleryName] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [reorderingGalleries, setReorderingGalleries] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleGalleryDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;

    const oldIndex = galleries.findIndex((g) => g.Id === active.id);
    const newIndex = galleries.findIndex((g) => g.Id === over.id);
    const reordered = arrayMove(galleries, oldIndex, newIndex);
    setGalleries(reordered);

    const orderData = reordered.map((g, i) => ({ id: g.Id, order: i + 1 }));
    setReorderingGalleries(true);
    try {
      await updateGalleryOrder(orderData);
    } catch {
      toast.error("Failed to save gallery order");
      setGalleries((prev) => prev); // revert handled by refetch
      refreshGalleries();
    } finally {
      setReorderingGalleries(false);
    }
  };

  useEffect(() => {
    // Load event info
    getEventById(id)
      .then((res) => setEvent(res.data?.Result || res.data))
      .catch(() => toast.error("Failed to load event details."))
      .finally(() => setLoadingEvent(false));

    // Load galleries for this event
    getGalleriesByEvent(id)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error("Failed to load galleries."))
      .finally(() => setLoadingGalleries(false));
  }, [id]);

  const handleGalleryClick = (gallery) => {
    navigate(`/events/${id}/gallery/${gallery.Id}`, { state: { gallery, chatRoomId: id } });
  };

  const refreshGalleries = () => {
    getGalleriesByEvent(id)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error("Failed to load galleries."));
  };

  const openEditGalleryModal = (gallery) => {
    setEditGalleryTarget(gallery);
    setEditGalleryName(gallery?.Name || "");
  };

  const handleEditGallery = async () => {
    const trimmed = editGalleryName.trim();
    if (!trimmed) {
      toast.warning("Gallery name is required.");
      return;
    }

    setActionLoading(true);
    try {
      await updateEventGallery(editGalleryTarget.Id, {
        EventId: Number(id),
        Name: trimmed,
      });
      toast.success("Gallery updated successfully");
      setEditGalleryTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to update gallery.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGallery = async () => {
    if (!deleteGalleryTarget) return;

    setActionLoading(true);
    try {
      await archiveEventGallery({
        galleryId: deleteGalleryTarget.Id,
        eventId: id,
        name: deleteGalleryTarget.Name,
      });
      toast.success("Gallery deleted");
      setDeleteGalleryTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to delete gallery.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loadingEvent) return <Loader />;

  return (
    <div>
      <Button variant="secondary" onClick={() => navigate("/events")} style={{ marginBottom: 20 }}>
        ← Back to Events
      </Button>

      {/* Event Info Card */}
      {event && (
        <div style={styles.eventCard}>
          {event.Thumbnail && (
            <img
              src={event.Thumbnail.replace("\\", "/")}
              alt={event.Name}
              style={styles.thumbnail}
            />
          )}
          <div style={styles.eventBody}>
            <div style={styles.badgeRow}>
              <span style={styles.badge}>{event.EventType}</span>
              {isAssigned && (
                <span style={styles.spBadge}>🎫 Sub-Promoter</span>
              )}
            </div>
            <h2 style={styles.title}>{event.Name}</h2>
            <div style={styles.row}><span style={styles.label}>📅</span> {event.FormattedDate}</div>
            <div style={styles.row}><span style={styles.label}>📍</span> {event.City}, {event.Country}</div>
            {event.Description && (
              <div style={{ ...styles.row, marginTop: 8, color: "#666", fontSize: 13 }}>{event.Description}</div>
            )}
          </div>
        </div>
      )}

      {/* Galleries Section */}
      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>Galleries</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!loadingGalleries && (
            <span style={styles.sectionCount}>{galleries.length} found</span>
          )}
          {reorderingGalleries && (
            <span style={{ fontSize: 12, color: "#6c63ff" }}>Saving order…</span>
          )}
          <button
            style={styles.watermarkBtn}
            onClick={() => setShowWatermark(true)}
            title="Promoter Watermark Settings"
          >
            <WatermarkIcon />
            <span>Watermark</span>
          </button>
          <Button onClick={() => setShowCreateGallery(true)} style={{ padding: "6px 14px", fontSize: 13 }}>
            + New Gallery
          </Button>
        </div>
      </div>

      {loadingGalleries ? (
        <Loader />
      ) : galleries.length === 0 ? (
        <p style={{ color: "#888", fontSize: 14 }}>No galleries found for this event.</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGalleryDragEnd}>
          <SortableContext items={galleries.map((g) => g.Id)} strategy={rectSortingStrategy}>
            <div style={styles.galleryGrid}>
              {galleries.map((gallery) => (
                <SortableGalleryCard
                  key={gallery.Id}
                  gallery={gallery}
                  onClick={() => handleGalleryClick(gallery)}
                  onEdit={openEditGalleryModal}
                  onDelete={(g) => setDeleteGalleryTarget(g)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {showCreateGallery && (
        <CreateGalleryModal
          eventId={id}
          onClose={() => setShowCreateGallery(false)}
          onCreated={() => {
            refreshGalleries();
          }}
        />
      )}

      {showWatermark && (
        <WatermarkSettingsModal
          onClose={() => setShowWatermark(false)}
          onSaved={() => {}}
        />
      )}

      {editGalleryTarget && (
        <EditNameModal
          title="Edit Gallery Name"
          label="Gallery Name"
          value={editGalleryName}
          onChange={setEditGalleryName}
          onSave={handleEditGallery}
          onCancel={() => !actionLoading && setEditGalleryTarget(null)}
          loading={actionLoading}
          saveText="Save"
        />
      )}

      {deleteGalleryTarget && (
        <ConfirmModal
          title="Delete Gallery"
          message={`Delete gallery "${deleteGalleryTarget.Name}"?`}
          confirmText="🗑"
          danger
          loading={actionLoading}
          onConfirm={handleDeleteGallery}
          onCancel={() => !actionLoading && setDeleteGalleryTarget(null)}
        />
      )}
    </div>
  );
};

const WatermarkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M7 17l3-4 3 4 2-3 2 3" />
    <circle cx="8.5" cy="8.5" r="1.5" />
  </svg>
);

const styles = {
  // Event card
  eventCard: {
    background: "#fff",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    maxWidth: 680,
    marginBottom: 36,
  },
  thumbnail: { width: "100%", height: 200, objectFit: "cover", display: "block" },
  eventBody: { padding: "20px 24px" },
  badgeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" },
  badge: {
    display: "inline-block",
    background: "#ede9ff", color: "#6c63ff",
    fontSize: 11, fontWeight: 600,
    padding: "2px 10px", borderRadius: 20,
    textTransform: "uppercase", letterSpacing: 0.5,
  },
  spBadge: {
    display: "inline-block",
    background: "#fff3cd", color: "#b45309",
    fontSize: 11, fontWeight: 700,
    padding: "2px 12px", borderRadius: 20,
    letterSpacing: 0.3,
    border: "1px solid #fcd34d",
  },
  title: { margin: "0 0 12px", fontSize: 20, fontWeight: 700, color: "#1e1e2d" },
  row: { marginBottom: 6, fontSize: 14, color: "#444", display: "flex", gap: 6, alignItems: "center" },
  label: { fontWeight: 600, color: "#1e1e2d" },

  watermarkBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "#6c63ff",
    background: "#ede9ff",
    border: "1.5px solid #c4b5fd",
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.15s",
    lineHeight: 1,
  },

  // Galleries section
  sectionHeader: { display: "flex", alignItems: "center", gap: 12, margin: "0 0 16px" },
  sectionTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  sectionCount: { fontSize: 13, color: "#888", background: "#f0f0f0", padding: "2px 10px", borderRadius: 20 },
  galleryGrid: { display: "flex", flexWrap: "wrap", gap: 16 },
  galleryCard: {
    background: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    cursor: "pointer",
    width: 240,
    transition: "transform 0.15s, box-shadow 0.15s",
  },
  colorBar: { height: 5 },
  galleryBody: { padding: "14px 16px 16px" },
  galleryTopRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 },
  galleryActions: { display: "flex", alignItems: "center", gap: 6 },
  galleryName: { fontWeight: 700, fontSize: 14, color: "#1e1e2d", flex: 1, marginRight: 8 },
  premiumBadge: {
    fontSize: 10, fontWeight: 700,
    background: "#fef3c7", color: "#d97706",
    padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
  },
  galleryCounts: { display: "flex", gap: 12, fontSize: 12, color: "#555", marginBottom: 8 },
  packageName: { fontSize: 12, color: "#6c63ff", marginBottom: 8 },
  galleryMeta: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 },
  cardActionBtn: {
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 7px",
    cursor: "pointer",
  },
  deleteActionBtn: {
    borderColor: "#fecaca",
    color: "#b91c1c",
    background: "#fff5f5",
  },
  tag: {
    fontSize: 10, background: "#f5f5f5", color: "#888",
    padding: "2px 8px", borderRadius: 20,
  },
};

export default EventDetails;
